import HojaArrugada from "@/components/portada/HojaArrugada";
import { useMuestra } from "@/components/portada/Pruebala";
import { useState } from "react";
import { COURSES } from "@/data/courses";

/**
 * LaRuta — el esquema de cómo se estudia aquí: competencia → curso → mapa → misión.
 *
 * POR QUÉ EXISTE. Gabri, 7 de octubre de 2026, después de ver perderse a sus alumnos: *«si
 * ponemos en la landing un esquema fácil que indique curso → módulo → lección»*. La plataforma
 * tiene cuatro palabras con jerarquía —están en `docs/estado/glosario.md`— y hasta entonces el
 * que llegaba tenía que deducirla de los menús.
 *
 * LAS CUATRO PALABRAS SON LAS DE GABRI (8 oct 2026): *«lo ideal sería quedarnos con las
 * palabras Competencia, Curso, Mapa, Misión»*. «Módulo» y «lección» son correctas y son de
 * profesor: dicen cómo está organizado el temario, no qué va a hacer el alumno. **Mapa** es lo
 * que se ve —un índice audiovisual, no una carpeta— y **misión** es lo que se hace. Las dos
 * viejas siguen vivas donde tienen que estar: en las claves que guarda el servidor (`module`,
 * `lessons`). Una clave guardada no es un nombre.
 *
 * Y CADA UNA LLEVA SU DEFINICIÓN DEBAJO. Sin ella, «competencia» se lee como un trámite
 * previo, y «mapa» y «misión» como dos palabras raras.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────
 * EL REDISEÑO DEL 19 DE OCTUBRE DE 2026, y es el que cambia lo que el esquema DICE.
 *
 * Era una fila de cuatro cajas con una flecha entre cada dos. Una flecha cuenta una
 * **secuencia**: que pasas de la competencia al curso como se pasa de un paso al siguiente.
 * Y es falso. No pasas: **estás dentro**. La misión está dentro del mapa, que está dentro del
 * curso, que está dentro de la competencia, y eso es lo que hay que ver de un vistazo para
 * que los menús de después no sorprendan. Gabri, eligiendo entre cuatro maquetas: *«mezcla de
 * r2 y r3»*. Así que las cajas se anidan y encogen hacia dentro.
 *
 * Y LA MITAD DERECHA ES LA MISMA RUTA, RELLENA. Es lo único de la portada que ata el esquema
 * abstracto a algo que el visitante acaba de hacer: la lección de muestra del hero. «Esto que
 * has hecho era una misión, y venía de aquí.»
 *
 * NADA DE ESO ESTÁ ESCRITO AQUÍ. Sale de `/api/portada/muestra`, que es la lección que marcas
 * en el CMS: el curso y el mapa vienen con ella, y la competencia se deriva de su `agentKey`
 * contra `COURSES`. Escribir «Latín esencial» a mano sería la clase de dato que se queda
 * viejo en silencio el día que cambies la muestra, y entonces el ejemplo miente justo en el
 * sitio donde dice «esto es de verdad».
 *
 * SIN MUESTRA —O SIN MAPA— LA DERECHA NO SE PINTA, y la izquierda se queda sola y centrada.
 * Media rejilla vacía es peor que una columna: es un hueco que parece un fallo de carga. Y es
 * la misma regla que ya siguen la muestra del hero y el catálogo en cifras.
 *
 * Y LLEGA TAPADA. Gabri, 20 de octubre de 2026: *«he pensado en hacer un text-reveal o algo
 * así: que a mano derecha haya un botón que ponga revelar y salga el ejemplo […] es para
 * darle un poco de vida y acción a la landing»*. El ejemplo gana con eso, y no solo de
 * adorno: enseñado de entrada son dos dibujos parecidos uno al lado del otro, y el de la
 * derecha se lee como una repetición del de la izquierda. Tapado es una pregunta —«¿y esto
 * con qué se rellena?»— y la respuesta llega cuando ya has leído el esquema.
 */
const PASOS = [
  ["Competencia", "La capacidad que desarrollas. Hay cinco."],
  ["Curso", "Desarrolla una habilidad, por grados: I, II, III."],
  ["Mapa", "El índice audiovisual de los contenidos."],
  ["Misión", "La lección de hoy. De cinco a diez minutos."],
];

/** El esquema: la palabra arriba y su definición al lado. En el ejemplo es al revés, y por
 *  eso los dos se arman de la misma lista en vez de escribirse dos veces. */
const ESQUEMA = PASOS.map(([titulo, pie]) => ({ titulo, pie }));

/** Las cajas, metidas unas dentro de otras y encogiendo. Se dibuja sola: el último escalón
 *  es el que lleva el rojo, porque es lo que se hace hoy.
 *
 *  CADA NIVEL ES `{ titulo, pie, fuerte }`, Y LAS DOS MITADES LO USAN IGUAL. Esto costó dos
 *  vueltas. La palabra va primero porque lo pidió Gabri —*«se debe añadir la palabra
 *  "competencia" antes de cultura y política»*—, y la primera versión la puso como un rótulo
 *  pequeño encima del nombre, con el nombre en grande. El cuerpo de letra era el mismo en las
 *  dos mitades, pero lo que iba EN ese cuerpo no: a la izquierda la palabra, a la derecha el
 *  nombre. Y un nombre de dieciocho letras a veintisiete píxeles pesa el doble que una palabra
 *  de once. Gabri: *«¿ves que cultura y política, latín esencial son demasiado grandes?
 *  deberían estar a la misma altura»*.
 *
 *  ASÍ QUE LA PALABRA ES EL TÍTULO EN LAS DOS, y lo que cambia es el renglón de al lado: a la
 *  izquierda la definición, a la derecha el nombre de verdad. Las dos tarjetas quedan línea
 *  por línea iguales, que es lo único que hace que se lean como la misma ruta. El nombre va en
 *  tinta entera y la definición en gris: mismo sitio y mismo tamaño, distinto peso. */
function Muneca({ niveles, i = 0 }) {
  const { titulo, pie, fuerte } = niveles[i];
  const ultimo = i === niveles.length - 1;
  return (
    <div
      className={`rounded-caja p-4 sm:p-5 ${ultimo
        ? "border border-alpha-red bg-alpha-red/[0.04]"
        : "border border-alpha-black/15"}`}
      data-testid={`ruta-nivel-${i + 1}`}
    >
      {/* UN RENGLON POR NIVEL, Y EL MISMO EN LAS DOS MITADES: es lo que hace que las dos
          tarjetas midan lo mismo y que la revelada no saque barra de desplazamiento -su alto
          no lo decide ella, se lo impone la de al lado-. */}
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className={`font-serif-display leading-tight ${ultimo ? "text-alpha-red" : ""}`}
              style={{ fontSize: `${27 - i * 2}px` }}>
          {titulo}
        </span>
        {/* Y LO QUE NO CABE SE CORTA CON PUNTOS SUSPENSIVOS. El titulo de una leccion de
            verdad es mas largo que una definicion de diccionario; recortarlo sin avisar
            seria dejar leer media frase creyendo que es la frase. */}
        {pie && (
          <span className={`text-[12.5px] leading-snug line-clamp-2
                            ${fuerte ? "text-alpha-black" : "text-alpha-black/55"}`}>
            {pie}
          </span>
        )}
      </div>
      {i + 1 < niveles.length && (
        <div className="mt-3 ml-4 sm:ml-5
                        -mr-[calc(1rem+1px)] -mb-[calc(1rem+1px)]
                        sm:-mr-[calc(1.25rem+1px)] sm:-mb-[calc(1.25rem+1px)]">
          <Muneca niveles={niveles} i={i + 1} />
        </div>
      )}
    </div>
  );
}

export default function LaRuta() {
  const muestra = useMuestra();
  const curso = muestra?.course || {};
  // LA COMPETENCIA SE DERIVA de la clave del mentor del curso, que es el slug de su
  // competencia. Es la misma traducción que hace la fila blanca, y por el mismo motivo: una
  // segunda tabla de nombres aquí se desincroniza sin que nadie lo diga.
  const suya = COURSES.find((c) => c.slug === curso.competencia);
  // CADA NIVEL DEL EJEMPLO LLEVA SU PALABRA DELANTE, y las cuatro salen de `PASOS`: si se
  // escribieran aqui, el dia que una palabra de la casa cambie las dos mitades dirian cosas
  // distintas de la misma ruta. Lo unico que cambia respecto al esquema es lo que va en el
  // renglon de al lado: alli una definicion, aqui un nombre de verdad, en tinta entera.
  const ejemplo = muestra && suya && muestra.module && curso.title && muestra.title
    ? [
        { titulo: PASOS[0][0], pie: suya.nombreCompetencia, fuerte: true },
        { titulo: PASOS[1][0], pie: curso.title, fuerte: true },
        { titulo: PASOS[2][0], pie: muestra.module, fuerte: true },
        { titulo: PASOS[3][0], fuerte: true,
          pie: `${String(muestra.posicion || 1).padStart(2, "0")} · ${muestra.title}` },
      ]
    : null;

  // AL REVELARLO, EL ESQUEMA SE VA Y EL EJEMPLO SE CENTRA. Gabri, 20 de octubre de 2026:
  // *«al darle al reveal, la tarjeta de ejemplo se desplace hacia el centro, la principal se
  // desvanezca de la pantalla y te queda centrada la tarjeta previamente arrugada»*. Y,
  // viendo la primera version: *«no me gusta que cambie el tamaño. se debe ver mas pequeño
  // como al principio. y las dos tarjetas, la de muestra y la de reveal, al mismo tamaño»*.
  //
  // POR ESO LA TARJETA NO CAMBIA DE ANCHO, SE MUEVE. El viaje es un `translateX` y no un
  // ensanchamiento: mide lo mismo antes y despues, y solo cambia de sitio. Lo de antes la
  // dejaba ocupar lo que soltaba el esquema y crecer hasta 720 px, que es justo lo que se
  // veia mal -la tarjeta que acabas de mirar deja de ser la misma-.
  //
  // Y EL ESQUEMA NO SE DESMONTA: se apaga quedandose donde esta. Si se quitara, la fila
  // perderia su alto y la que queda daria un tiron vertical. `visibility: hidden` -en el
  // CSS- lo saca del orden de tabulacion y de lo que lee un lector de pantalla sin mover un
  // pixel, que es la diferencia entre estar oculto y no estar.
  const [abierto, setAbierto] = useState(false);

  return (
    <div data-testid="la-ruta">
      <div className="font-mono-alpha text-[10px] uppercase tracking-[0.22em] text-alpha-red mb-5">
        Cómo se estudia aquí
      </div>
      <div className={`ruta-par ${!ejemplo ? "max-w-[720px]" : ""}
                       ${abierto ? "ruta-par--sola" : ""}`}>
        <div className="ruta-par__esquema rounded-ficha bg-white p-5 sm:p-6"
             aria-hidden={abierto}>
          <Muneca niveles={ESQUEMA} />
        </div>
        {ejemplo && (
          <HojaArrugada
            testid="ruta-ejemplo"
            etiqueta="Revelar"
            pista="Ver ejemplo con la lección de muestra de arriba."
            onAbrir={() => setAbierto(true)}
            className="ruta-par__ejemplo"
          >
            {/* LA TARJETA NO SE DIBUJA AQUI: el fondo, el radio y el relleno son de la
                hoja, que es la que tiene que medir lo mismo abierta que cerrada. Poniendo
                una tarjeta propia dentro habia dos, con dos rellenos distintos.

                Y DENTRO NO HAY NI ENCABEZADO NI PIE. Los habia —«La misma ruta, con
                nombres» arriba y «Es la lección de muestra de arriba» abajo— y Gabri los
                quitó el 20 de octubre de 2026: *«para evitar hacer scroll en ella»*. Los dos
                decían lo que el botón ya dice ANTES de pulsarlo, y aquí gastaban dos
                renglones de un alto que esta tarjeta no decide —se lo impone la de al
                lado—. Un rótulo que repite la puerta por la que acabas de entrar no informa:
                ocupa. */}
            <Muneca niveles={ejemplo} />
          </HojaArrugada>
        )}
      </div>
    </div>
  );
}
